import React, { useEffect, useState } from 'react';
import styles from 'components/nonPodiatry/page.module.scss';
import Button from 'components/common/button';
import { StateType } from 'reducer';
import { actionTypesOfReasonForVisit } from 'reducer/actionType';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { useDispatch } from 'react-redux';
import { PodiatryConditionalOptionsType } from 'types/types';
import { ActiveTypeNonPodiatry, usePersistNonPodiatryReducer } from 'reducer/nonPodiatry';
import clsx from 'clsx';
import MetaTag from 'components/meta';
import InputText from 'components/common/InputText';
import { useScrollIntoView } from 'hooks/useScroll';

type Props = {
  options: PodiatryConditionalOptionsType[] | PodiatryConditionalOptionsType;
  nextPage: number;
  currentPage?: number;
  actionPage: string;
  multiple?: boolean;
  isFilter?: boolean;
  required?: boolean;
  textFieldForOther?: boolean;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function CommonPage({
  options,
  nextPage,
  nextOrSubmitButton,
  actionPage,
  multiple = true,
  required = true,
  isFilter = false,
  textFieldForOther = false,
}: Props) {
  const { errorTagRefs, scrollToError } = useScrollIntoView();
  const ReduxDispatch = useDispatch();
  const [inputText, setInputText] = useState('');
  const [inputTextError, setInputTextError] = useState(false);
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState<string | null>('');
  const [questionList, setQuestionList] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [error, setError] = useState<boolean>(false);

  const [state, dispatch] = usePersistNonPodiatryReducer();

  useEffect(() => {
    if (isFilter && Array.isArray(options)) {
      const findOptions = options.find(
        (obj: PodiatryConditionalOptionsType) => obj.name === actionPage,
      );
      if (findOptions) {
        try {
          setQuestionList(findOptions.options);
          setTitle(findOptions.title);
          setSubTitle(findOptions.subTitle);
        } catch (error) {
          console.error('🚀 ~ Error on filter options', error);
        }
      }
    } else if (!Array.isArray(options)) {
      setQuestionList(options.options);
      setTitle(options.title);
      setSubTitle(options.subTitle);
    }
  }, [actionPage, isFilter, options]);

  useEffect(() => {
    if (state) {
      const data = state.find((object: StateType) => object.name === actionPage);
      if (data) {
        setSelectedItems(data.value);
        if (data.text) setInputText(data.text);
      }
      if (actionTypesOfReasonForVisit.REASON_FOR_VISIT === actionPage) {
        ReduxDispatch(
          appStateSettingsAction({ reasonForVisit: data.value.toString() }),
        );
      }
    }
  }, [ReduxDispatch, actionPage, state]);

  const onSelectOptions = (item: string) => {
    const preventItems = ['None', 'No major life changes', 'Not applicable'];
    if (multiple) {
      if (preventItems.includes(item)) {
        setSelectedItems([item]);
      } else {
        setSelectedItems((old) => {
          if (preventItems.includes(old.toString())) {
            return [item];
          } if (old.find((value) => value === item)) {
            return old.filter((data) => data !== item);
          } return [...old, item];
        });
      }
    } else {
      setSelectedItems([item]);
    }
    setError(false);
    setInputTextError(false);
  };

  const clickNextOrSubmit = async () => {
    if (required && selectedItems.length === 0) {
      setError(true);
      scrollToError(0);
      return;
    }

    const other = selectedItems.find((item) => item === 'Other');
    if (other && textFieldForOther && !inputText.trim()) {
      setInputTextError(true);
      return;
    }

    const dispatchPayload: ActiveTypeNonPodiatry = {
      type: actionPage,
      name: title,
      value: selectedItems,
    };
    if (inputText && other) {
      dispatchPayload.text = inputText.trim();
    }

    if (actionTypesOfReasonForVisit.REASON_FOR_VISIT === actionPage) {
      ReduxDispatch(
        appStateSettingsAction({ reasonForVisit: selectedItems.toString() }),
      );
    }
    await dispatch(dispatchPayload);
    nextOrSubmitButton(nextPage);
  };

  const onChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);
    setInputTextError(false);
  };

  return (
    <>
      <MetaTag title={title} />
      <h2 ref={errorTagRefs[0]} className={`marginTopZero ${styles.titleText}`}>{title}</h2>
      {subTitle && <p className={styles.titleTextSubText}>{subTitle}</p>}
      {error && (
      <p className="error-message">
        Please select at least one option.
      </p>
      )}

      <div>
        {questionList.map((item) => {
          const isSelected = selectedItems.includes(item);
          return (
            <div key={item}>
              <p
                className={clsx('option', { 'selected-option': isSelected })}
                onClick={() => onSelectOptions(item)}
                onKeyUp={() => {}}
              >
                {item}
              </p>
              {(textFieldForOther && item === 'Other' && isSelected) && (
              <InputText
                value={inputText}
                placeholder="Type..."
                onChange={onChangeText}
                error={inputTextError}
                maxLength={160}
                isWidthFull
              />
              )}
            </div>
          );
        })}
      </div>
      <div className="next-button-fixed">
        <Button onClick={clickNextOrSubmit}>
          Next
        </Button>
      </div>
    </>
  );
}

export default CommonPage;
